import { REDIRECT_AFTER_LOGIN_KEY } from '@app/features/oidc/redirectAfterLogin';
import { MasterLayout } from '@app/layouts/MasterLayout';
import { captureError } from '@app/lib/Sentry';
import UseRoutingContext from '@context/routing/useRoutingContext';
import { isRouteExist } from '@context/routing/utils/route-checker';
import { useWebPushApiCallback } from '@context/web-push';
import useTrackLocation from '@hooks/useTrackLocation';
import { useEffect, useRef } from 'react';
import { Navigate, useNavigate, useRoutes } from 'react-router-dom';
import { WebPushSetup } from '../features/web-push';

// const appRoutes = [
//   {
//     path: 'users',
//     children: [
//       { path: '', element: <UsersList /> },
//       { path: 'new', element: <CreateUser /> },
//       { path: ':id/edit', element: <EditUser /> },
//     ],
//   },
//   {
//     path: 'clients',
//     children: [
//       { path: '', element: <ClientsList /> },
//       { path: 'new', element: <CreateClient /> },
//       { path: ':id/edit', element: <EditClient /> },
//     ],
//   },
//   {
//     path: 'caregivers',
//     children: [
//       { path: '', element: <CaregiversList /> },
//       { path: 'new', element: <CreateCaregiver /> },
//       { path: ':id/edit', element: <EditCaregiver /> },
//     ],
//   },

//   {
//     path: 'availability',
//     element: <AvailabilityRoutes.AvailabilityPage />,
//   },
//   {
//     path: 'scheduling',
//     element: <SchedulingRoutes.SchedulingPage />,
//   },
//   {
//     path: 'shifts',
//     element: <ShiftsRoutes.ShiftsPage />,
//   },

//   {
//     path: '*',
//     element: <NotFound />,
//   },
// ];

const PrivateRoutes = () => {
  const navigate = useNavigate();
  const onToken = useWebPushApiCallback();
  const hasExecutedRef = useRef(false); // Persist across re-renders (Strict Mode)

  const { getIndexRouteKey, getAllPaths, toReactRouter } = UseRoutingContext();

  useTrackLocation();
  const routes = useRoutes(
    [
      {
        path: 'dashboard',
        element: (
          <WebPushSetup onToken={onToken}>
            <MasterLayout />
          </WebPushSetup>
        ),
        children: toReactRouter, // dynamic routes under /dashboard
      },
      { path: '*', element: <Navigate to="/404" replace /> },
    ]
    // location
  );

  useEffect(() => {
    const checkNotificationFlag = async () => {
      // Check if we came from a notification click
      try {
        const { get, del } = await import('idb-keyval');
        const flag = await get('fromNotificationClick');

        if (flag?.value) {
          await del('fromNotificationClick');
          // if came from notification, clear any previous redirect from keycloak to go to notifications list with modal open
          sessionStorage.removeItem(REDIRECT_AFTER_LOGIN_KEY);

          navigate('/dashboard/notifications/list');
          return true;
        }
      } catch (error) {
        captureError(error as Error, {
          context: 'PrivateRoutes - check notification flag',
        });
      }
      return false;
    };

    const handleRoute = async () => {
      // Prevent double execution in React Strict Mode (development only)
      if (hasExecutedRef.current) {
        return;
      }
      hasExecutedRef.current = true;

      const isFromNotification = await checkNotificationFlag();
      if (isFromNotification) return; // Don't redirect

      // Check if we're already on a valid route with or without query params
      const currentPathOnly = window.location.pathname;

      // If we're on a specific route (not just root or dashboard), don't redirect
      // This preserves any direct URL access with query params
      if (currentPathOnly !== '/' && currentPathOnly !== '/dashboard' && currentPathOnly !== '/dashboard/') {
        // Clear the stored redirect since we're already on a valid page
        sessionStorage.removeItem(REDIRECT_AFTER_LOGIN_KEY);
        return;
      }

      const prevRoute = sessionStorage.getItem(REDIRECT_AFTER_LOGIN_KEY);

      const indexRoute = getIndexRouteKey as string;

      if (!prevRoute) {
        navigate(indexRoute);
        return;
      }

      const allPaths = getAllPaths();
      const exist = isRouteExist(prevRoute, allPaths);

      if (exist) {
        navigate(prevRoute);
        // Clear after navigating
        sessionStorage.removeItem(REDIRECT_AFTER_LOGIN_KEY);
      } else {
        navigate(indexRoute);
      }
    };

    handleRoute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <>{routes}</>;
};

export default PrivateRoutes;
